/**
 * Client mirrors of grant_customer_credit per-grant caps (KAN-784,
 * supabase/migrations/20261013130100_grant_credit_configurable_caps.sql):
 *   - admin   → the platform ceiling ($25,000, private.cvx_credit_platform_ceiling)
 *   - billing → company_settings.credit_billing_grant_cap; missing / invalid →
 *               $1,000 (the server fails CLOSED the same way); never above the
 *               ceiling.
 * There is no per-customer balance cap and no 30-day per-actor cap any more.
 *
 * UX ONLY — the SECURITY DEFINER RPC re-enforces these. Keep the rules in
 * lockstep with private.cvx_credit_setting_amount. Prefer the conservative
 * (billing) cap when the role is unknown.
 */

export const CREDIT_PLATFORM_CEILING = 25000;
export const BILLING_GRANT_CAP = 1000;           // default when the setting is unset/invalid
export const ADMIN_GRANT_CAP = CREDIT_PLATFORM_CEILING;
export const DEFAULT_CREDIT_ALERT_THRESHOLD = 1000;

/** company_settings.settings keys (owner-editable; see update_company_settings). */
export const CREDIT_BILLING_GRANT_CAP_KEY = 'credit_billing_grant_cap';
export const CREDIT_ALERT_THRESHOLD_KEY = 'credit_alert_threshold';

/**
 * One dollar-amount credit setting, or `fallback` when it is missing, not a
 * number, more than 2 decimals, negative, zero (unless `allowZero`), or above
 * the ceiling — the same fail-closed rule as the server.
 */
export function creditSettingAmount(settings, key, fallback, allowZero = false) {
  const v = settings ? settings[key] : undefined;
  if (typeof v !== 'number' || !Number.isFinite(v)) return fallback;
  if (Math.round(v * 100) / 100 !== v) return fallback;
  if (v < 0 || (v === 0 && !allowZero) || v > CREDIT_PLATFORM_CEILING) return fallback;
  return v;
}

/** The per-grant cap for `role` in a tenant whose settings blob is `settings`. */
export function grantCapForRole(role, settings) {
  if (role === 'admin') return ADMIN_GRANT_CAP;
  return creditSettingAmount(settings, CREDIT_BILLING_GRANT_CAP_KEY, BILLING_GRANT_CAP, false);
}

/** The large-credit alert threshold for a tenant (0 = every grant alerts). */
export function creditAlertThreshold(settings) {
  return creditSettingAmount(settings, CREDIT_ALERT_THRESHOLD_KEY, DEFAULT_CREDIT_ALERT_THRESHOLD, true);
}

/**
 * OVER_ROLE_LIMIT copy: billing is told to ask an admin; admin is told the
 * numeric per-grant max (they already are the top role).
 */
export function overRoleLimitMessageId(role) {
  return role === 'admin'
    ? 'billing.credits.rpcError.overRoleLimitAdmin'
    : 'billing.credits.rpcError.overRoleLimit';
}
